class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      // A11y (WCAG 2.4.3): move keyboard focus into the dialog as soon as it is shown, without
      // depending on transitionend (unreliable in this theme). Focus the close button — a
      // natively focusable control that reliably holds focus — so keyboard/screen-reader users
      // land inside the drawer; the focus trap below keeps them contained.
      requestAnimationFrame(() => {
        const focusTarget = this.querySelector('.drawer__close') || this.querySelector('.drawer__inner');
        if (focusTarget && typeof focusTarget.focus === 'function') focusTarget.focus();
      });
    });

    this.addEventListener(
      'transitionend',
      () => {
        this.trapCartDrawerFocus();
      },
      { once: true }
    );

    // A11y (WCAG 2.1.1 / 2.1.2 / 2.4.3): transitionend above is the primary hook to move
    // focus into the dialog and trap it, but some environments/animations do not emit a
    // reliable `transitionend`. When that happens focus stays outside the drawer, Tab can
    // reach the page behind the overlay, and Escape (whose listener is on this element)
    // never receives the keyup. This fallback guarantees focus enters the dialog after the
    // open animation, and only acts if focus is not already inside (no regression when
    // transitionend does fire).
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.trapCartDrawerFocus();
    }, 350);

    // Ensure Escape closes the drawer even in the brief window before focus has moved inside.
    if (!this.escapeKeyHandler) {
      this.escapeKeyHandler = (event) => {
        if (event.key === 'Escape' || event.code === 'Escape') this.close();
      };
    }
    document.addEventListener('keyup', this.escapeKeyHandler);

    document.body.classList.add('overflow-hidden');
  }

  trapCartDrawerFocus() {
    const containerToTrapFocusOn = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : document.getElementById('CartDrawer');
    const focusElement = this.querySelector('.drawer__close') || this.querySelector('.drawer__inner');
    if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    if (this.escapeKeyHandler) document.removeEventListener('keyup', this.escapeKeyHandler);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
